import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { useParams } from 'react-router-dom';
import config from "../common/Config";
import generatePickerListHistoryPDF from './PickerListHistoryPDF';
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { Iconify } from "src/components/iconify";
import '../common/commonStyles.css';

const firstName = localStorage.getItem('first_name');
const lastName = localStorage.getItem('last_name');

const ViewPicking = () => {
  const { picklistNo } = useParams();
  const [newOrders, setNewOrders] = useState([]);
  const [pickingDetails, setPickingDetails] = useState({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [picker, setSelectedPicker] = useState('');
  const [draw, setDraw] = useState(1);
  const [start, setStart] = useState(0);
  const [length, setLength] = useState(10000);
  const accessToken = localStorage.getItem('accessToken');
  const todayDate = new Date();

  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function formatNewDate(dateString) {
    if (dateString == '0000-00-00' || !dateString) {
      return '';
    } else {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }

  useEffect(() => {
    async function fetchData() {
      const formData = new FormData();
      formData.append('searchByFromdate', fromDate);
      formData.append('searchByTodate', toDate);
      formData.append('draw', draw);
      formData.append('start', start);
      formData.append('length', length);
      formData.append('pickerId', picker);
      formData.append('picklist_number', picklistNo);

      if (accessToken === null) {
        window.location.href = `${config.BASE_URL}/login`;
        return;
      }
      try {
        const responseHistory = await fetch(`${config.API_URL}/get_view_picking_history`, {
          method: 'POST',
          headers: {
            'authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (!responseHistory.ok) {
          throw new Error('Failed to fetch picking history');
        }

        const historyData = await responseHistory.json();
        setNewOrders(historyData.data || []);

        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const picklistHeaders = { authorization: `Bearer ${accessToken}` };
        let picklistBody;
        if (useEncrypt) {
          const ep = encryptPayload({
            searchByFromdate: fromDate,
            searchByTodate: toDate,
            draw: String(draw),
            start: String(start),
            length: String(length),
            pickerId: picker,
            picklist_number: picklistNo,
          });
          if (!ep) {
            setPickingDetails({});
            return;
          }
          const fd = new FormData();
          fd.append("encrypted_payload", ep);
          picklistBody = fd;
        } else {
          picklistBody = formData;
        }

        const responseDetails = await fetch(`${config.API_URL}/get_view_picklist`, {
          method: 'POST',
          headers: picklistHeaders,
          body: picklistBody,
        });

        if (!responseDetails.ok) {
          throw new Error('Failed to fetch picking details');
        }

        const detailsJson = await responseDetails.json();
        const detailsData = normalizeEncryptedApiResponse(detailsJson, useEncrypt) ?? detailsJson;
        setPickingDetails(detailsData.data || {});
      } catch (error) {
      }
    }

    fetchData();
  }, [fromDate, toDate, picker, picklistNo, draw, start, length]);

  const handleGeneratePickerListHistoryPDF = () => {
    generatePickerListHistoryPDF(picklistNo);
  }

  const getFormattedReferenceNos = () => {
    const refs = pickingDetails.reference_no || [];
    return refs.length > 0 ? refs.map(ref => ref.reference_no).join(', ') : 'N/A';
  };

  const getFormattedAccountNos = () => {
    const accs = pickingDetails.accound_no || [];
    return accs.length > 0 ? accs.map(acc => acc.accound_no).join(', ') : 'N/A';
  };

  const getRowColor = (row) => {
    if (row.discount == '100.00' || row.discount == 100.00) {
      return '#f14240';
    } else if (row.picked_qty != row.quantity) {
      return '#00B8D9';
    }
    return '#212b36';
  };

  const filteredAccounts = (pickingDetails.accound_no || []).filter(row => row.note && row.note.trim() !== '');

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ py: 1, px: 1, borderBottom: "1px solid rgba(145, 158, 171, 0.16)", display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1.5, sm: 0 } }}>
          <Typography className="page-title">
            View Picking: {picklistNo}
          </Typography>
          <Button
            variant="contained"
            className="button-variant-contained"
            startIcon={<Iconify icon="eva:printer-fill" width={18} height={18} />}
            onClick={handleGeneratePickerListHistoryPDF}
            sx={{

              width: { xs: "100%", sm: "auto" },

            }}
          >
            Print
          </Button>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, borderBottom: "1px solid rgba(145, 158, 171, 0.16)" }}>
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
              <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Box
                  component="img"
                  src={`${config.BASE_URL}/assets/img/${accessToken}_logo.png`}
                  alt="Logo"
                  sx={{
                    width: accessToken === 'knm' || accessToken === 'eve' || accessToken === 've' ? { xs: '40%', sm: '30%' } : 'auto',
                    maxWidth: '200px'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: "center", sm: "left" } }}>
                  {pickingDetails?.shop_details?.shop_name || ''}
                </Typography>
                <Typography sx={{ color: "#000", fontSize: { xs: "0.7rem", sm: "0.75rem" }, mb: 0.5, textAlign: { xs: "center", sm: "left" } }}>
                  {pickingDetails?.shop_details?.address || ''}
                </Typography>
                <Typography sx={{ color: "#000", fontSize: { xs: "0.7rem", sm: "0.75rem" }, textAlign: { xs: "center", sm: "left" } }}>
                  <strong>Phone No:</strong> {pickingDetails?.shop_details?.phone || 'N/A'} | <strong>Email:</strong> {pickingDetails?.shop_details?.email || 'N/A'}
                </Typography>
                <Typography sx={{ color: "#000", fontSize: { xs: "0.7rem", sm: "0.75rem" }, textAlign: { xs: "center", sm: "left" } }}>
                  <strong>Website:</strong> {pickingDetails?.shop_details?.website || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 0.5 } }}>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Order Number:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {getFormattedReferenceNos()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Account Number:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {getFormattedAccountNos()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Route Number:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {pickingDetails.route_number ? pickingDetails.route_number.map(route => route.route_number).join(', ') : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 0.5 } }}>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Picking List:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {picklistNo}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Delivery Date:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {pickingDetails.reference_no && pickingDetails.reference_no[0] && pickingDetails.reference_no[0].deliverydate ? formatNewDate(pickingDetails.reference_no[0].deliverydate) : 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Print By:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {firstName} {lastName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.25, sm: 0.5 }, alignItems: { xs: "flex-start", sm: "center" } }}>
                    <Typography className="innvoice-title">
                      Print Date:
                    </Typography>
                    <Typography className="innvoice-subtitle">
                      {formatDate(todayDate)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <TableContainer
            >
              <Table size="small" className="table-cell-border"
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell sx={{ width: '100px' }}>Code</TableCell>
                    <TableCell sx={{ width: '400px' }}>Product Name</TableCell>
                    <TableCell sx={{ width: '80px' }}>Type</TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'right' }}>Order Qty</TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'right' }}>Pick Qty</TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'right' }}>Accept Qty</TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'left' }}>Sales Person</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newOrders.map((item, index) => {
                    const rowColor = getRowColor(item);
                    return (
                      <TableRow
                        key={item.code || index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#F9FAFB"
                          }
                        }}
                      >
                        <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.code}
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor, wordBreak: 'break-word' }}>
                          {item.name}
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.order_type}
                        </TableCell>
                        <TableCell align="right" sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.quantity}
                        </TableCell>
                        <TableCell align="right" sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.picked_qty}
                        </TableCell>
                        <TableCell align="right" sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.confirmed_total}
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: rowColor }}>
                          {item.sale_person}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {filteredAccounts && filteredAccounts.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, color: '#212B36' }}
              >
                Account Details
              </Typography>
              <TableContainer
                sx={{
                  overflowX: "auto",
                  '&::-webkit-scrollbar': {
                    width: '10px',
                    height: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#F4F6F8',
                    borderRadius: '5px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c1c1c1',
                    borderRadius: '5px',
                    border: '2px solid #F4F6F8',
                    '&:hover': {
                      backgroundColor: '#a8a8a8',
                    },
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#c1c1c1 #F4F6F8',
                }}
              >
                <Table size="small" sx={{
                  minWidth: { xs: 500, sm: '100%' },
                  "& .MuiTableCell-root": {
                    borderBottomColor: "rgba(145, 158, 171, 0.16)",
                    borderBottomStyle: 'dashed'
                  }
                }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "rgba(145, 158, 171, 0.08)" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#919eab", py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 } }}>A/c No</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#919eab", py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 } }}>Comment</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#919eab", py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 } }}>Comment By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((item) => {
                      const rows = [];
                      if (item.staff_note) {
                        rows.push(
                          <TableRow
                            key={`${item.id}-staff-note`}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#F9FAFB"
                              }
                            }}
                          >
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36" }}>{item.accound_no}</TableCell>
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36", wordBreak: 'break-word' }}>{item.staff_note}</TableCell>
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36" }}>{item.created_by}</TableCell>
                          </TableRow>
                        );
                      }
                      if (item.note) {
                        rows.push(
                          <TableRow
                            key={`${item.id}-note`}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#F9FAFB"
                              }
                            }}
                          >
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36" }}>{item.accound_no}</TableCell>
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36", wordBreak: 'break-word' }}>{item.note}</TableCell>
                            <TableCell sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#212b36" }}>{item.created_by}</TableCell>
                          </TableRow>
                        );
                      }
                      return rows;
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Paper>

    </Box>
  );
};

export default ViewPicking;
